import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DialogModule} from 'primeng/dialog';
import {
    SearchFilterComponent,
    FilterConfig
} from '@components/search-filter/search-filter.component';
import {
    DashboardKeyMetricsService,
    GetDashboardLiveAlertsRequest,
    DashboardLiveAlert,
    GetLiveAlertByNotificationResponse
} from '@services/dashboard-key-metrics.service';
import {Subscription} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {AlertView} from '@/common/Enum/AlertType.enum';

@Component({
    selector: 'app-live-alerts',
    standalone: true,
    imports: [CommonModule, DialogModule, SearchFilterComponent],
    templateUrl: './live-alerts.component.html',
    styleUrl: './live-alerts.component.scss'
})
export class LiveAlertsComponent implements OnInit, OnDestroy {
    userId: number | null = null;
    filterConfig: FilterConfig = {
        fields: [],
        showFarmFilter: true,
        showHouseFilter: true
    };

    dialogVisible = false;
    selectedAlert: GetLiveAlertByNotificationResponse | null = null;
    dialogLoading = false;

    private currentFilter: {farmId?: number; farmHouseId?: number} = {};
    private pollSub?: Subscription;
    private readonly refreshMs = 60000;
    isLoading = false;
    private isDestroyed = false;
    resolvingId: number | null = null;

    constructor(
        private dashboardService: DashboardKeyMetricsService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        const detail = this.getUserDetailFromLocalStorage();
        this.userId = detail?.id ?? null;
        this.startPolling();
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        this.stopPolling();
    }

    private getUserDetailFromLocalStorage(): any | null {
        if (
            typeof window !== 'undefined' &&
            typeof window.localStorage !== 'undefined'
        ) {
            const userDetailString = localStorage.getItem('UserDetail');
            return userDetailString ? JSON.parse(userDetailString) : null;
        }
        return null;
    }

    openAlertDetails(alert: AlertView) {
        const notificationId = alert.notificationId;
        if (!notificationId) {
            this.toastr.error(
                'Cannot load alert details: missing notification ID'
            );
            return;
        }

        this.dialogLoading = true;
        this.dialogVisible = true;

        this.dashboardService
            .getLiveAlertByNotification(notificationId)
            .subscribe({
                next: (response) => {
                    this.selectedAlert = response;
                    this.dialogLoading = false;
                },
                error: (error) => {
                    console.error('Failed to load alert details:', error);
                    this.toastr.error('Failed to load alert details');
                    this.dialogLoading = false;
                    this.dialogVisible = false;
                }
            });
    }

    closeAlertDetails() {
        this.dialogVisible = false;
        this.selectedAlert = null;
        this.dialogLoading = false;
    }

    alertsAll: AlertView[] = [];
    filteredAlerts: AlertView[] = [];

    private startPolling(params?: GetDashboardLiveAlertsRequest) {
        this.stopPolling();
        this.scheduleNextPoll(params);
    }

    private scheduleNextPoll(params?: GetDashboardLiveAlertsRequest) {
        if (this.isDestroyed || this.isLoading) {
            return;
        }

        const p = params ?? this.buildParamsFromFilter();
        this.isLoading = true;

        this.dashboardService.getDashboardLiveAlerts(p).subscribe({
            next: (resp) => {
                if (!this.isDestroyed) {
                    this.alertsAll = this.mapToView(resp.slice(0, 5) || []);
                    this.filteredAlerts = [...this.alertsAll];
                    this.isLoading = false;

                    setTimeout(() => {
                        if (!this.isDestroyed) {
                            this.scheduleNextPoll();
                        }
                    }, this.refreshMs);
                }
            },
            error: (error) => {
                if (!this.isDestroyed) {
                    console.warn('Failed to load live alerts:', error);
                    this.alertsAll = [];
                    this.filteredAlerts = [];
                    this.isLoading = false;

                    setTimeout(
                        () => {
                            if (!this.isDestroyed) {
                                this.scheduleNextPoll();
                            }
                        },
                        Math.min(this.refreshMs / 2, 10000)
                    );
                }
            }
        });
    }

    private stopPolling() {
        this.isLoading = false;
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = undefined;
        }
    }

    private buildParamsFromFilter(): GetDashboardLiveAlertsRequest {
        const params: GetDashboardLiveAlertsRequest = {};
        if (this.currentFilter.farmId)
            params.farmId = this.currentFilter.farmId;
        if (this.currentFilter.farmHouseId)
            params.houseId = this.currentFilter.farmHouseId;
        return params;
    }

    private mapToView(items: DashboardLiveAlert[]): AlertView[] {
        return items.map((it) => {
            const {typeText, levelText} = this.parseAlertType(it.alertType);
            const severity = 'critical';
            const scope =
                it.location ||
                `${it.farmName ?? ''} / ${it.farmHouseName ?? ''}`.trim();
            const ts = it.timestamp;
            const id = (it as any).notificationId ?? (it as any).id;
            return {
                title: typeText,
                value: it.value != null ? String(it.value) : null,
                timestamp: ts,
                scope: scope,
                aiInsight: it.aiInsight,
                severity,
                timeAgo: ts ? this.formatTimeAgo(ts) : '',
                notificationId: typeof id === 'number' ? id : undefined
            };
        });
    }

    private parseAlertType(html: string | undefined): {
        typeText: string;
        levelText: string;
    } {
        const raw = html || '';
        const spanMatch = raw.match(/<span[^>]*>(.*?)<\/span>/i);
        const levelText = spanMatch?.[1]?.trim() || '';
        // Strip all tags for readable text
        const typeText = raw.replace(/<[^>]*>/g, '').trim();
        return {typeText, levelText};
    }

    private formatTimeAgo(iso: string): string {
        // const then = new Date(iso).getTime();
        const then = new Date(iso + 'Z').getTime();
        const now = Date.now();
        const diff = Math.max(0, now - then);
        const mins = Math.floor(diff / 60000);
        if (mins < 60) return `${mins} min ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
        const days = Math.floor(hrs / 24);
        return `${days} day${days === 1 ? '' : 's'} ago`;
    }

    onFilter(values: any) {
        if (this.isLoading) {
            return;
        }

        const farmId = values?.farmId ? Number(values.farmId) : undefined;
        const houseId = values?.farmHouseId
            ? Number(values.farmHouseId)
            : undefined;

        const nextFilter = {farmId, farmHouseId: houseId};
        const changed =
            this.currentFilter.farmId !== nextFilter.farmId ||
            this.currentFilter.farmHouseId !== nextFilter.farmHouseId;
        if (!changed) {
            return;
        }
        this.currentFilter = nextFilter;
        const params: GetDashboardLiveAlertsRequest = {};
        if (farmId) params.farmId = farmId;
        if (houseId) params.houseId = houseId;
        this.startPolling(params);
    }

    onClearFilters() {
        if (this.isLoading) {
            return;
        }

        this.currentFilter = {};
        this.startPolling();
    }

    onResolve(alert: AlertView) {
        const id = alert.notificationId;
        if (!id) {
            this.toastr.error('Cannot resolve: missing notification id');
            return;
        }
        if (this.resolvingId) return;
        this.resolvingId = id;
        this.dashboardService.resolveAlert(id).subscribe({
            next: () => {
                this.alertsAll = this.alertsAll.filter(
                    (a) => a.notificationId !== id
                );
                this.filteredAlerts = this.filteredAlerts.filter(
                    (a) => a.notificationId !== id
                );
                this.toastr.success('Alert resolved');
                this.resolvingId = null;
            },
            error: () => {
                this.toastr.error('Failed to resolve alert');
                this.resolvingId = null;
            }
        });
    }
}
